import { Box, Typography } from '@mui/material'
import { useState } from 'react'
import NodeBox from './common/NodeBox'
import Chart from '@assets/flowchart/Chart.svg'
import NodeSelect from './common/NodeSelect'
import NodeInput from './common/NodeInput'
import NodeCheckBox from './common/NodeCheckBox'
import { useNodeData } from './common/useNodeData'
import { useAppTheme } from '@/theme/ThemeContext'
import {
  consumptionKindOptions,
  HOUR_KIND_ID,
  isWindowKind,
  windowHourOptions,
} from '@/alerts/consumptionKind'
import { COMPARISON_OPERATORS } from '@/alerts/comparisonOperators'
import {
  CONSUMPTION_PERIOD_OPTIONS,
  consumptionPeriod,
  consumptionPeriodPatch,
  type Period,
} from '@/alerts/hourlyAlert'
import {
  addRow,
  calendarDraftProblems,
  describeCalendar,
  type Calendar,
  type Grain,
} from '@/alerts/calendar'
import CalendarDialog from '../calendar/CalendarDialog'
import { useFlowState } from '../FlowStateContext'

// Core (in-scope) controls: consumption type (firstSelectedId), operator
// (secondSelectedId), and the fixed reference value (value). These map to
// `condition.consumption` { consumption_type, time_range, operator,
// reference_value }.
//
// The comparison-model controls (units, the comparison row — basis select,
// value, average select, "compare to" checkbox — and the equivalent-period
// checkbox) are mapped end-to-end and enabled. Units defaults to '2' (קוב׳)
// when unset. Deferred features are REMOVED from the UI rather than shown
// disabled (operator decision 2026-07-15; each returns WITH its feature):
// the weeks period option returns with the weeks engine feature, and the
// payer-relearn checkbox (`use_previous_payers_for_average`) returns with
// the payer-relearn feature. The max/min basis options were removed earlier
// (plan 005). The Layer-A completeness gates for the weeks and max/min
// combinations are retained as safety nets for legacy drafts. "Shared"
// consumption (firstSelectedId '3') is fully supported by the engine and
// publishable — its Layer-A gate was removed.
// The schema forbids an average source on an hour check.
const HOUR_NO_COMPARISON =
  'בדיקה שעתית לא תומכת בהשוואה לממוצע או לתקופה מקבילה'

const ConsumptionNode = ({ id, data }: { id: string; data: any }) => {
  const patch = useNodeData(id)
  const { mode } = useAppTheme()
  const { treeCalendar } = useFlowState()
  const calendar = data?.calendar as Calendar | undefined
  const [calendarOpen, setCalendarOpen] = useState(false)
  const labelSx = {
    color: mode === 'dark' ? '#FFFFFF' : '#003F61',
    fontFamily: 'Rubik, sans-serif',
    fontSize: '14px',
    fontWeight: 300,
  }
  const secondOptions = COMPARISON_OPERATORS.map(({ id, label }) => ({
    id,
    label,
  }))
  const isHour = consumptionPeriod(data ?? {}) === 'hour'
  const grain: Grain = isHour ? 'hour' : 'day'
  // An hour check compares with one fixed limit; a percent needs an average.
  const thirdOptions = [
    { id: '1', label: '%' },
    { id: '2', label: 'קוב׳' },
    { id: '3', label: 'ל׳' },
  ].filter(option => !isHour || option.id !== '1')
  // max/min comparison is a deferred feature (product decision 2026-07-15):
  // removed from the options entirely. The completeness rule for
  // fourthSelectedId '2'/'3' stays as a safety net for legacy drafts.
  const fourthOptions = [{ id: '1', label: 'ממוצע הצריכה' }]
  // The weeks period is a deferred feature (operator decision 2026-07-15):
  // removed from the options entirely; id '1' (weeks) keeps its completeness
  // gate for legacy drafts. חודשים stays as the sole option.
  const fifthOptions = [{ id: '2', label: 'חודשים' }]
  return (
    <NodeBox title="מאפיין צריכה" icon={Chart} nodeId={id}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <NodeSelect
          placeholder="מאפיין"
          options={thirdOptions}
          value={data?.thirdSelectedId ?? '2'}
          onChangeValue={v => patch({ thirdSelectedId: v })}
        />
        <NodeInput
          value={data?.value ?? ''}
          onChange={v => patch({ value: v })}
        />
        <NodeSelect
          placeholder="מאפיין"
          options={secondOptions}
          value={data?.secondSelectedId ?? null}
          onChangeValue={v => patch({ secondSelectedId: v })}
        />
        <NodeSelect
          placeholder="מאפיין"
          options={consumptionKindOptions(data?.firstSelectedId)}
          value={isHour ? HOUR_KIND_ID : (data?.firstSelectedId ?? null)}
          onChangeValue={v => {
            if (v === HOUR_KIND_ID)
              patch(consumptionPeriodPatch('hour', data ?? {}))
            else
              patch(
                isHour
                  ? {
                      ...consumptionPeriodPatch('day', data ?? {}),
                      firstSelectedId: v,
                    }
                  : { firstSelectedId: v }
              )
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'flex-end',
          marginTop: '16px',
        }}
      >
        <NodeSelect
          placeholder="תקופה"
          options={CONSUMPTION_PERIOD_OPTIONS}
          value={isHour ? 'hour' : 'day'}
          onChangeValue={v =>
            v && patch(consumptionPeriodPatch(v as Period, data ?? {}))
          }
        />
        <Typography sx={labelSx}>בכל</Typography>
      </Box>
      {!isHour && isWindowKind(data?.firstSelectedId) && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginTop: '16px',
          }}
        >
          <NodeSelect
            placeholder="שעה"
            options={windowHourOptions('end', data?.windowEnd)}
            value={data?.windowEnd ?? null}
            onChangeValue={v => patch({ windowEnd: v })}
          />
          <Typography sx={labelSx}>עד</Typography>
          <NodeSelect
            placeholder="שעה"
            options={windowHourOptions('start', data?.windowStart)}
            value={data?.windowStart ?? null}
            onChangeValue={v => patch({ windowStart: v })}
          />
          <Typography sx={labelSx}>משעה</Typography>
        </Box>
      )}
      {
        <>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              justifyContent: 'flex-end',
              marginTop: '16px',
            }}
          >
            <NodeSelect
              placeholder="מאפיין"
              options={fifthOptions}
              value={data?.fifthSelectedId ?? null}
              onChangeValue={v => patch({ fifthSelectedId: v })}
              disabled={isHour}
            />
            <NodeInput
              value={data?.secondValue ?? ''}
              onChange={v => patch({ secondValue: v })}
              disabled={isHour}
            />
            <NodeSelect
              placeholder="מאפיין"
              options={fourthOptions}
              value={data?.fourthSelectedId ?? null}
              onChangeValue={v => patch({ fourthSelectedId: v })}
              disabled={isHour}
            />
            <NodeCheckBox
              text="בהשוואה ל"
              checked={!isHour && (data?.firstChecked ?? false)}
              disabled={isHour}
              onChange={e => patch({ firstChecked: e.target.checked })}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <NodeCheckBox
              text="ביחס לתקופה מקבילה בשנה הקודמת (אם אין אז ביחס לחודש עד 3 חודשים אחרונים)"
              checked={!isHour && (data?.secondChecked ?? false)}
              disabled={isHour}
              onChange={e => patch({ secondChecked: e.target.checked })}
            />
          </Box>
          {isHour && (
            <Typography sx={{ ...labelSx, fontSize: '12px', opacity: 0.7 }}>
              {HOUR_NO_COMPARISON}
            </Typography>
          )}
          {!isHour && data?.secondChecked && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end',
                marginTop: '8px',
              }}
            >
              <NodeInput
                text="מספר חודשים לתקופה מקבילה"
                value={String(
                  data?.equivalentMonths ??
                    data?.kept?.equivalent_period_months ??
                    1
                )}
                onChange={v => patch({ equivalentMonths: v })}
              />
            </Box>
          )}
          {!isHour && String(data?.thirdSelectedId ?? '2') === '1' && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'flex-end',
                marginTop: '16px',
              }}
            >
              <NodeInput
                text="גבול עליון (קוב׳)"
                value={String(data?.maxValue ?? '')}
                onChange={v => patch({ maxValue: v })}
              />
              <NodeInput
                text="גבול תחתון (קוב׳)"
                value={String(data?.minValue ?? '')}
                onChange={v => patch({ minValue: v })}
              />
            </Box>
          )}
        </>
      }
      <Box
        sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}
      >
        <NodeCheckBox
          text="הגבלה לימים ושעות"
          checked={calendar !== undefined}
          onChange={e => {
            patch({
              calendar: e.target.checked ? addRow(undefined) : undefined,
            })
            setCalendarOpen(e.target.checked)
          }}
        />
      </Box>
      {calendar ? (
        <CalendarLine
          calendar={calendar}
          grain={grain}
          onEdit={() => setCalendarOpen(true)}
        />
      ) : (
        treeCalendar && (
          <Typography
            dir="rtl"
            sx={{ ...labelSx, fontSize: '12px', opacity: 0.7 }}
          >
            {`לפי לוח העץ: ${describeCalendar(treeCalendar)}`}
          </Typography>
        )
      )}
      <CalendarDialog
        open={calendarOpen}
        title="לוח זמנים לבדיקה"
        note="מחליף את לוח הזמנים של העץ בבדיקה הזו"
        grain={grain}
        value={calendar}
        onChange={next => patch({ calendar: next })}
        onRemove={() => {
          patch({ calendar: undefined })
          setCalendarOpen(false)
        }}
        onClose={() => setCalendarOpen(false)}
      />
    </NodeBox>
  )
}

// One line under the box: the check's calendar, or its first draft problem in red, and the edit link.
const CalendarLine = ({
  calendar,
  grain,
  onEdit,
}: {
  calendar: Calendar
  grain: Grain
  onEdit: () => void
}) => {
  const dark = useAppTheme().mode === 'dark'
  const problem = calendarDraftProblems(calendar, grain)[0]
  const text = problem ?? describeCalendar(calendar)
  const lineSx = {
    fontFamily: 'Rubik, sans-serif',
    fontSize: '14px',
    lineHeight: '21px',
    color: dark ? '#FFFFFF' : '#003F61',
  }
  return (
    <Box dir="rtl" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Typography
        data-testid="check-calendar-summary"
        noWrap
        title={text}
        sx={{
          ...lineSx,
          fontWeight: 300,
          minWidth: 0,
          color: problem ? (dark ? '#F44336' : '#D32F2F') : lineSx.color,
        }}
      >
        {text}
      </Typography>
      <Box
        component="button"
        type="button"
        className="nodrag"
        onClick={onEdit}
        sx={{
          ...lineSx,
          flexShrink: 0,
          fontWeight: 400,
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
      >
        עריכה
      </Box>
    </Box>
  )
}

export default ConsumptionNode
